import { ErrorHandler, Injectable } from '@angular/core';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import * as Sentry from '@sentry/browser';
import { environment } from 'apps/klickdata/src/environments/environment';
import { ApplicationService } from './application.service';
import { ConnectivityService } from './connectivity/connectivity.service';
import { isNetworkFailure } from './connectivity/is-network-failure';
import { ErrorDialogService } from './error-dialog/error-dialog.service';
import { LoggerService } from './logger/logger.service';

Sentry.init({
    dsn: 'https://cffbc5a6998c4d548be108f1f02e2417@sentry.klickdata.se/2',
    allowUrls: [
        /.+?\.k3\.io/,
        /.+?\.klickdata\.se/,
        /.+?\.klickportalen\.se/,
        /.+?\.utbildning\.online/,
        /.+?\.growknowledge\.net/,
    ],
    environment: environment.buildMode,
    ignoreErrors: ['Non-Error exception captured'],
});

@Injectable({ providedIn: 'root' })
export class AppErrorHandler extends ErrorHandler {
    protected errorReported = false;

    constructor(
        protected app: ApplicationService,
        protected auth: AuthService,
        protected config: ConfigService,
        protected logger: LoggerService,
        protected dialog: ErrorDialogService,
        protected connectivity: ConnectivityService
    ) {
        super();
    }

    /**
     * Handled error by @see HttpErrorService throwError @see ErrorResponse
     * None handled error of type @see HttpErrorResponse
     * Handle chunk loading errors that occur when:
     * - New version is deployed while user has old version open
     * - Lazy-loaded modules fail to load due to cache issues
     * - Webpack chunks are missing or have different hashes
     * @param error - The error to handle
     */
    handleError(error: any): void {
        if (this.isChunkLoadError(error) && navigator.onLine && !isNetworkFailure(error)) {
            // Failed lazy imports after a network drop look like chunk errors.
            // Reload only when a new app version is already known.
            if (this.app.hasPendingVersionUpdate()) {
                console.warn('Chunk load error detected. Clearing cache and reloading...', error);
                this.handleChunkError();
            }
            return;
        }

        if (isNetworkFailure(error) || !navigator.onLine || (this.isChunkLoadError(error) && !navigator.onLine)) {
            this.connectivity.markOffline();
            return;
        }

        const status = this.extractStatusCode(error);
        this.logger.log('status: ', status);

        if (status === 401) {
            this.auth.handleInvalidToken();
        } else if (status === 403) {
            this.auth.handleForbidden();
        } else if (status === 500) {
            this.dialog.open(error?.error?.error?.messages);
        } else {
            if (this.config.config.buildMode === 'local') {
                throw error;
            } else {
                Sentry.configureScope((scope) =>
                    scope.addEventProcessor((event) => {
                        event.release = this.app.getVersionValue();
                        return event;
                    })
                );
                Sentry.captureException(
                    error instanceof Error
                        ? error
                        : new Error(JSON.stringify(error.error || error.message || error))
                );
                this.dialog.open(Sentry.lastEventId());
            }
        }

        this.errorReported = true;
    }

    /**
     * Extract the status code from the error
     * @param error - The error to extract the status code from
     * @returns The status code
     */
    private extractStatusCode(error: any): number {
        if (!error) return 0;

        if (typeof error.status === 'number' && error.status !== 0) return error.status;
        if (typeof error.statusCode === 'number') return error.statusCode;
        if (error.error?.statusCode) return error.error.statusCode;

        if (error.rejection) return this.extractStatusCode(error.rejection);

        if (typeof error === 'string' || error.message) {
            const errorString = typeof error === 'string' ? error : error.message;
            const jsonMatch = errorString.match(/\{[\s\S]*"statusCode"\s*:\s*(\d+)[\s\S]*\}/);
            if (jsonMatch) {
                try {
                    const parsed = JSON.parse(jsonMatch[0]);
                    return parsed.error?.statusCode || parsed.statusCode || 0;
                } catch {
                    const statusMatch = errorString.match(/"statusCode"\s*:\s*(\d+)/);
                    if (statusMatch) return parseInt(statusMatch[1], 10);
                }
            }
        }

        return error.status || 0;
    }

    // FIX: combined single-pass check, removed redundant errorName === 'ChunkLoadError'
    /**
     * Check if the error is a chunk load error
     * @param error - The error to check
     * @returns True if the error is a chunk load error, false otherwise
     */
    private isChunkLoadError(error: any): boolean {
        if (!error) return false;

        const combined = `${error.message || ''} ${error.name || ''} ${error.toString?.() || ''}`;

        const chunkLoadPatterns = [
            /Loading chunk [\d]+ failed/i,
            /ChunkLoadError/i,
            /Loading CSS chunk [\d]+ failed/i,
            /Failed to fetch dynamically imported module/i,
            /Importing a module script failed/i,
            /error loading dynamically imported module/i,
            /Failed to load module script/i,
        ];

        return chunkLoadPatterns.some((pattern) => pattern.test(combined));
    }

    // FIX: timestamp guard (15s) instead of permanent flag
    // FIX: Sentry reporting before reload
    // FIX: cache-busting fallback URL
    /**
     * Handle chunk load error by clearing all caches and reloading
     */
    private handleChunkError(): void {
        const lastReload = sessionStorage.getItem('chunk-error-reload');
        const now = Date.now();

        if (lastReload && now - parseInt(lastReload) < 15_000) {
            Sentry.captureMessage('ChunkLoadError reload loop detected', Sentry.Severity.Warning);
            console.error('Chunk reload loop detected — aborting.');
            return;
        }

        Sentry.captureMessage('ChunkLoadError: clearing cache and reloading', Sentry.Severity.Info);
        sessionStorage.setItem('chunk-error-reload', String(now));

        this.app.reload().catch(() => {
            window.location.href = this.app.getCacheBustingReloadUrl();
        });
    }
}